import { action, makeObservable, observable, runInAction } from "mobx";
import HrmsAPI from "../api/HrmsAPI";
import { FilterValue } from "../../../models/Filters/FilterValue";
import {
  EmployeeCountsResult,
  HrmsCampaignOption,
  HrmsEmployee,
  HrmsEmployeeCountsParams,
  HrmsEmployeeListParams,
  HrmsSyncEventRow,
  HrmsSyncEventsListParams,
} from "../../../models/Employees/HrmsEmployee";

export class HrmsStore {
  hrmsAPI: HrmsAPI;
  employees: HrmsEmployee[];
  limit: number;
  total_pages: number;
  total_count: number;
  counts: EmployeeCountsResult;
  isLoadingList: boolean;
  isLoadingCounts: boolean;
  syncEvents: HrmsSyncEventRow[];
  syncEventsPage: number;
  syncEventsLimit: number;
  syncEventsTotalCount: number;
  isLoadingSyncEvents: boolean;
  syncEventsErrorMessage: string | null;
  errorMessage: string | null;
  campaignOptionRows: HrmsCampaignOption[];
  campaignOptionsError: string | null;
  /** Dropdown value. Observable so the list re-renders when the choice changes. */
  selectedCampaigns: FilterValue[];
  /**
   * Typed search text. Not observable: the inputs own the letters, and observing
   * them would redraw the employee table on every keystroke.
   */
  searchDraft: string;
  contactDraft: string;
  campaignQuery: string;
  searchInput: HTMLInputElement | null;
  contactInput: HTMLInputElement | null;
  listSessionKey: string;
  private employeeListRequestId = 0;
  private employeeCountsRequestId = 0;
  private campaignOptionsRequestId = 0;
  private customerSearchRequestId = 0;
  private searchTimer: ReturnType<typeof setTimeout> | null = null;
  private contactTimer: ReturnType<typeof setTimeout> | null = null;
  private campaignTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.hrmsAPI = new HrmsAPI();
    this.employees = [];
    this.limit = 30;
    this.total_pages = 0;
    this.total_count = 0;
    this.counts = {
      active: "0",
      inactive: "0",
      new_employees: "0",
      total: 0,
    };
    this.isLoadingList = false;
    this.isLoadingCounts = false;
    this.syncEvents = [];
    this.syncEventsPage = 1;
    this.syncEventsLimit = 20;
    this.syncEventsTotalCount = 0;
    this.isLoadingSyncEvents = false;
    this.syncEventsErrorMessage = null;
    this.errorMessage = null;
    this.campaignOptionRows = [];
    this.campaignOptionsError = null;
    this.selectedCampaigns = [];
    this.searchDraft = "";
    this.contactDraft = "";
    this.campaignQuery = "";
    this.searchInput = null;
    this.contactInput = null;
    this.listSessionKey = "";

    makeObservable(this, {
      employees: observable,
      limit: observable,
      total_pages: observable,
      total_count: observable,
      counts: observable,
      isLoadingList: observable,
      isLoadingCounts: observable,
      syncEvents: observable,
      syncEventsPage: observable,
      syncEventsLimit: observable,
      syncEventsTotalCount: observable,
      isLoadingSyncEvents: observable,
      syncEventsErrorMessage: observable,
      errorMessage: observable,
      campaignOptionRows: observable,
      campaignOptionsError: observable,
      selectedCampaigns: observable,
      getEmployees: action,
      getEmployeeCounts: action,
      getSyncEvents: action,
      setSyncEventsPage: action,
      setSyncEventsLimit: action,
      getCampaignOptions: action,
      beginListFilters: action,
      setSelectedCampaigns: action,
      reset: action,
    });
  }

  reset = () => {
    this.employees = [];
    this.limit = 30;
    this.total_pages = 0;
    this.total_count = 0;
    this.counts = {
      active: "0",
      inactive: "0",
      new_employees: "0",
      total: 0,
    };
    this.isLoadingList = false;
    this.isLoadingCounts = false;
    this.syncEvents = [];
    this.syncEventsPage = 1;
    this.syncEventsLimit = 20;
    this.syncEventsTotalCount = 0;
    this.isLoadingSyncEvents = false;
    this.syncEventsErrorMessage = null;
    this.errorMessage = null;
    this.campaignOptionRows = [];
    this.campaignOptionsError = null;
    this.clearFilterTimers();
    this.selectedCampaigns = [];
    this.searchDraft = "";
    this.contactDraft = "";
    this.campaignQuery = "";
    this.searchInput = null;
    this.contactInput = null;
    this.listSessionKey = "";
  };

  /** Seeds the filter drafts once per visit. Later URL updates must not wipe what is typed. */
  beginListFilters = (
    sessionKey: string,
    input: {
      searchText: string;
      contactNumber: string;
      selectedCampaigns: FilterValue[];
    },
  ) => {
    this.listSessionKey = sessionKey;
    this.searchDraft = input.searchText;
    this.contactDraft = input.contactNumber;
    this.campaignQuery = "";
    this.selectedCampaigns = input.selectedCampaigns;
  };

  setSelectedCampaigns = (items: FilterValue[]) => {
    this.selectedCampaigns = items;
  };

  /** Drops a customer-search response when a newer search has already started. */
  nextCustomerSearchRequestId = () => {
    this.customerSearchRequestId += 1;
    return this.customerSearchRequestId;
  };

  isCurrentCustomerSearch = (requestId: number) =>
    requestId === this.customerSearchRequestId;

  clearFilterTimers = () => {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    if (this.contactTimer) clearTimeout(this.contactTimer);
    if (this.campaignTimer) clearTimeout(this.campaignTimer);
    this.searchTimer = null;
    this.contactTimer = null;
    this.campaignTimer = null;
  };

  queueSearchDraft = (raw: string, apply: (value: string) => void) => {
    this.searchDraft = raw;
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => apply(raw.trim()), 400);
  };

  queueContactDraft = (raw: string, apply: (value: string) => void) => {
    this.contactDraft = raw;
    if (this.contactTimer) clearTimeout(this.contactTimer);
    this.contactTimer = setTimeout(() => apply(raw.trim()), 400);
  };

  queueCampaignQuery = (raw: string, apply: (value: string) => void) => {
    this.campaignQuery = raw;
    if (this.campaignTimer) clearTimeout(this.campaignTimer);
    this.campaignTimer = setTimeout(() => apply(this.campaignQuery.trim()), 400);
  };

  clearCampaignQuery = () => {
    this.campaignQuery = "";
    if (this.campaignTimer) clearTimeout(this.campaignTimer);
    this.campaignTimer = null;
  };

  getEmployees = async (params: HrmsEmployeeListParams) => {
    const requestId = ++this.employeeListRequestId;
    this.isLoadingList = true;
    this.errorMessage = null;
    try {
      const response = await this.hrmsAPI.getEmployees(params);
      if (requestId !== this.employeeListRequestId) return response;
      runInAction(() => {
        this.employees = response.result?.list || [];
        this.limit = response.result?.limit || params.limit;
        this.total_count = response.result?.total_count || 0;
        this.total_pages = response.result?.total_pages || 0;
        this.isLoadingList = false;
      });
      return response;
    } catch (error: any) {
      if (requestId === this.employeeListRequestId) {
        runInAction(() => {
          this.errorMessage =
            error?.response?.data?.message || "Failed to fetch employees list.";
          this.isLoadingList = false;
        });
      }
      throw error;
    }
  };

  getEmployeeCounts = async (params: HrmsEmployeeCountsParams) => {
    const requestId = ++this.employeeCountsRequestId;
    this.isLoadingCounts = true;
    try {
      const response = await this.hrmsAPI.getEmployeeCounts(params);
      if (requestId !== this.employeeCountsRequestId) return response;
      runInAction(() => {
        if (response?.result) this.counts = response.result;
        this.isLoadingCounts = false;
      });
      return response;
    } catch (error: any) {
      if (requestId === this.employeeCountsRequestId) {
        runInAction(() => {
          this.isLoadingCounts = false;
        });
      }
      throw error;
    }
  };

  setSyncEventsPage = (page: number) => {
    this.syncEventsPage = page;
  };

  setSyncEventsLimit = (limit: number) => {
    this.syncEventsLimit = limit;
    this.syncEventsPage = 1;
  };

  getCampaignOptions = async (customerId: number, search: string) => {
    const requestId = ++this.campaignOptionsRequestId;
    this.campaignOptionsError = null;
    try {
      const rows = await this.hrmsAPI.getCampaignOptions({
        customer_id: customerId,
        search,
      });
      if (requestId !== this.campaignOptionsRequestId) return;
      runInAction(() => {
        this.campaignOptionRows = rows;
      });
    } catch {
      if (requestId !== this.campaignOptionsRequestId) return;
      runInAction(() => {
        this.campaignOptionRows = [];
        this.campaignOptionsError = "Failed to search campaigns.";
      });
    }
  };

  getSyncEvents = async (params: HrmsSyncEventsListParams) => {
    this.isLoadingSyncEvents = true;
    this.syncEventsErrorMessage = null;
    try {
      const response = await this.hrmsAPI.getSyncEvents(params);
      this.syncEvents = response?.result?.rows || [];
      this.syncEventsPage = response?.result?.pagination?.page || params.page;
      this.syncEventsLimit = response?.result?.pagination?.limit || params.limit;
      this.syncEventsTotalCount = response?.result?.pagination?.total || 0;
      return response;
    } catch (error: any) {
      this.syncEventsErrorMessage =
        error?.response?.data?.message || "Failed to fetch GetKnit sync events.";
      throw error;
    } finally {
      this.isLoadingSyncEvents = false;
    }
  };
}
